import { BASE_API_URL } from '../apiConfig';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import { SPORT_ROLES, getRoleBadge } from '../utils/sports';
import { useTranslation } from 'react-i18next';

const LeagueMembers = () => {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [members, setMembers] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [league, setLeague] = useState(null);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '', action: null, targetId: null });
    const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [editLevel, setEditLevel] = useState(3);
    const [editRoles, setEditRoles] = useState([]);

    const loadMembers = async () => {
        const token = localStorage.getItem('token');
        try {
            const roleRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/role`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const roleData = await roleRes.json();
            setIsAdmin(roleData.is_admin);

            if (!roleData.is_admin) {
                navigate(`/league/${leagueId}`);
                return;
            }

            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/members`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMembers(data);
            }

            const leagueRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (leagueRes.ok) {
                const leagueData = await leagueRes.json();
                setLeague(leagueData);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        loadMembers();
    }, [leagueId]);

    const handleToggleAdminRequest = (userId, currentStatus) => {
        const adminCount = members.filter(m => m.is_admin).length;
        if (currentStatus && adminCount <= 1) {
            setModalConfig({
                isOpen: true,
                title: t('attention'),
                message: t('cannot_remove_last_admin'),
                isAlert: true
            });
            return;
        }

        setModalConfig({
            isOpen: true,
            title: currentStatus ? t('remove_admin') : t('make_admin'),
            message: t('confirm_role_change'),
            action: 'toggleAdmin',
            targetId: userId,
            currentStatus: currentStatus
        });
    };

    const handleRemoveRequest = (userId, isUserAdmin) => {
        const adminCount = members.filter(m => m.is_admin).length;
        if (isUserAdmin && adminCount <= 1) {
            setModalConfig({
                isOpen: true,
                title: t('attention'),
                message: t('cannot_remove_last_admin'),
                isAlert: true
            });
            return;
        }

        setModalConfig({
            isOpen: true,
            title: t('remove_participant'),
            message: t('remove_participant_ask'),
            action: 'remove',
            targetId: userId
        });
    };

    const handleUpdateLevelAndRoles = async (userId, newLevel, newRoles) => {
        const token = localStorage.getItem('token');
        const currentMember = members.find(m => m.id === userId);
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/members/${userId}/level`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    level: newLevel !== undefined ? newLevel : currentMember.level,
                    roles: newRoles !== undefined ? newRoles : (currentMember.roles || [])
                })
            });
            if (res.ok) {
                setMembers(prev => prev.map(m => m.id === userId ? {
                    ...m,
                    level: newLevel !== undefined ? newLevel : m.level,
                    roles: newRoles !== undefined ? newRoles : m.roles
                } : m));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const openEditModal = (member) => {
        setEditingMember(member);
        setEditLevel(member.level || 3);
        setEditRoles(member.roles || []);
        setEditMemberModalOpen(true);
    };

    const handleSaveMemberEdit = async () => {
        await handleUpdateLevelAndRoles(editingMember.id, editLevel, editRoles);
        setEditMemberModalOpen(false);
    };

    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 480);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleConfirmAction = async () => {
        const token = localStorage.getItem('token');
        const { action, targetId, currentStatus } = modalConfig;

        try {
            let res;
            if (action === 'toggleAdmin') {
                res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/members/${targetId}/role`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ is_admin: !currentStatus })
                });
            } else if (action === 'remove') {
                res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/members/${targetId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            if (res && res.ok) {
                setModalConfig({ isOpen: false });
                loadMembers();
            } else if (res) {
                const errData = await res.json();
                setModalConfig({ ...modalConfig, isAlert: true, message: errData.error || t('error_operation') });
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="league-page">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '10px' }}>
                <h2 style={{ flex: 1, margin: 0 }}>{isMobile ? t('participants') : t('manage_participants')}</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="primary-btn outline" onClick={() => navigate(`/league/${leagueId}/player-links`)} style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', padding: isMobile ? '6px 10px' : '8px 16px' }}>
                        🔗 {isMobile ? t('links') : t('manage_links')}
                    </button>
                    <button className="secondary-btn" onClick={() => navigate(`/league/${leagueId}`)} style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', padding: isMobile ? '6px 10px' : '8px 16px' }}>
                        ← {isMobile ? t('league') : t('back_to_league')}
                    </button>
                </div>
            </div>

            <div className="card" style={{ padding: isMobile ? '12px' : '24px', transition: 'none', transform: 'none' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: isMobile ? '0' : '10px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left', fontSize: isMobile ? '0.85rem' : '1rem' }}>
                                <th style={{ padding: isMobile ? '8px 4px' : '10px' }}>{t('first_name')}</th>
                                <th style={{ padding: isMobile ? '8px 4px' : '10px' }}>{isMobile ? t('level_short') : t('level')}</th>
                                <th style={{ padding: isMobile ? '8px 4px' : '10px' }}>{isMobile ? t('role_short') : t('role')}</th>
                                {!isMobile && <th style={{ padding: '10px' }}>{t('league_role')}</th>}
                                <th style={{ padding: isMobile ? '8px 4px' : '10px', textAlign: 'right' }}>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(member => (
                                <tr key={member.id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: isMobile ? '12px 4px' : '10px', verticalAlign: 'middle', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                        {isMobile ? (
                                            <strong>{member.first_name} {member.last_name ? member.last_name.charAt(0) + '.' : ''}</strong>
                                        ) : (
                                            <>{member.first_name} {member.last_name}</>
                                        )}
                                        {isMobile && member.is_admin && <span style={{ marginLeft: '5px', color: '#0d47a1' }}>👑</span>}
                                    </td>
                                    <td style={{ padding: isMobile ? '12px 4px' : '10px', verticalAlign: 'middle' }}>
                                        <span style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontVariantNumeric: 'tabular-nums', fontWeight: 'bold' }}>
                                            {member.level || 3}
                                        </span>
                                    </td>
                                    <td style={{ padding: isMobile ? '12px 4px' : '10px', verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                                            {(member.roles || []).map(r => (
                                                <div key={r} className={`role-badge ${r.charAt(0).toUpperCase()}`} title={t(r)} style={{ minWidth: '18px', width: 'auto', padding: '0 3px', height: '18px', fontSize: '9px' }}>
                                                    {getRoleBadge(r, t)}
                                                </div>
                                            ))}
                                            {(!member.roles || member.roles.length === 0) && <span style={{ color: '#999', fontSize: '0.8rem' }}>-</span>}
                                        </div>
                                    </td>
                                    {!isMobile && (
                                        <td style={{ padding: '10px', verticalAlign: 'middle' }}>
                                            {member.is_admin ? (
                                                <span style={{ background: '#e3f2fd', color: '#0d47a1', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold' }}>{t('admin')}</span>
                                            ) : (
                                                <span style={{ background: '#f5f5f5', color: '#616161', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem' }}>{t('player')}</span>
                                            )}
                                        </td>
                                    )}
                                    <td style={{ padding: isMobile ? '12px 4px' : '10px', textAlign: 'right', verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', gap: isMobile ? '5px' : '10px', justifyContent: 'flex-end' }}>
                                            <button
                                                className="secondary-btn"
                                                onClick={() => openEditModal(member)}
                                                style={{ padding: isMobile ? '6px 8px' : '8px 12px', fontSize: isMobile ? '0.7rem' : '0.9rem' }}
                                                title={t('edit_level_roles')}
                                            >
                                                ✏️ {isMobile ? '' : t('edit')}
                                            </button>
                                            <button
                                                className="secondary-btn"
                                                onClick={() => handleToggleAdminRequest(member.id, member.is_admin)}
                                                style={{ padding: isMobile ? '6px 8px' : '8px 12px', fontSize: isMobile ? '0.7rem' : '0.9rem', whiteSpace: 'nowrap' }}
                                                title={member.is_admin ? "- " + t('admin') : "+ " + t('admin')}
                                            >
                                                {member.is_admin ? "- " + t('admin') : "+ " + t('admin')}
                                            </button>
                                            <button
                                                className="danger-btn outline"
                                                style={{ padding: isMobile ? '6px 8px' : '6px 12px', fontSize: isMobile ? '0.7rem' : '1.1rem' }}
                                                onClick={() => handleRemoveRequest(member.id, member.is_admin)}
                                                title={t('remove_participant')}
                                            >
                                                🗑
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
                onConfirm={handleConfirmAction}
                title={modalConfig.title}
                message={modalConfig.message}
                isAlert={modalConfig.isAlert}
            />

            {editMemberModalOpen && (
                <ConfirmationModal
                    isOpen={editMemberModalOpen}
                    onClose={() => setEditMemberModalOpen(false)}
                    title={`✏️ ${t('edit')} ${editingMember?.first_name}`}
                    onConfirm={handleSaveMemberEdit}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', textAlign: 'left' }}>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>{t('competitive_level_label')}</label>
                            <select
                                value={editLevel}
                                onChange={(e) => setEditLevel(parseInt(e.target.value))}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                            >
                                {[1, 2, 3, 4, 5].map(lvl => <option key={lvl} value={lvl}>{t('level')} {lvl}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>{t('preferred_roles_label')}</label>
                            <div className="role-menu-list" style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '5px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                {(league ? SPORT_ROLES[league.sport] || [] : []).map(r => (
                                    <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer' }} className="role-item">
                                        <input
                                            type="checkbox"
                                            checked={editRoles.includes(r)}
                                            onChange={e => {
                                                const next = e.target.checked
                                                    ? [...editRoles, r]
                                                    : editRoles.filter(role => role !== r);
                                                setEditRoles(next);
                                            }}
                                        />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '0.9rem' }}>{t(r)}</span>
                                            <div className={`role-badge ${r.charAt(0).toUpperCase()}`} style={{ minWidth: '20px', width: 'auto', padding: '0 4px', height: '20px', fontSize: '10px' }}>
                                                {getRoleBadge(r, t)}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </ConfirmationModal>
            )}
        </div>
    );
};

export default LeagueMembers;
